// src/pages/admin/Dashboard.jsx

import React, { useState, useEffect, useRef } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { 
  FaBriefcase, 
  FaNewspaper, 
  FaTrophy,
  FaGraduationCap,
  FaUserTie,
  FaCalendarAlt,
  FaEye,
  FaDownload,
  FaFileExcel,
  FaTimes,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaBuilding,
  FaUserGraduate,
  FaBriefcase as FaRole
} from "react-icons/fa";
import "./Dashboard.css";
import SummaryApi from "../../components/common/index";

// ── Mock Data (Keep these for the charts) ──────────────────
const monthlyData = [
  { month: "Jan", visitors: 4200, applications: 320, blogs: 58, revenue: 18400 },
  { month: "Feb", visitors: 5100, applications: 410, blogs: 72, revenue: 22100 },
  { month: "Mar", visitors: 4800, applications: 375, blogs: 65, revenue: 20500 },
  { month: "Apr", visitors: 6300, applications: 520, blogs: 88, revenue: 27800 },
  { month: "May", visitors: 7100, applications: 610, blogs: 95, revenue: 31200 },
  { month: "Jun", visitors: 6800, applications: 580, blogs: 91, revenue: 29600 },
  { month: "Jul", visitors: 7800, applications: 670, blogs: 102, revenue: 34500 },
  { month: "Aug", visitors: 8400, applications: 720, blogs: 115, revenue: 37900 },
  { month: "Sep", visitors: 7600, applications: 640, blogs: 108, revenue: 33200 },
  { month: "Oct", visitors: 9200, applications: 810, blogs: 124, revenue: 42100 },
  { month: "Nov", visitors: 8900, applications: 780, blogs: 119, revenue: 40300 },
  { month: "Dec", visitors: 10500, applications: 920, blogs: 138, revenue: 48600 },
];

const pieData = [
  { name: "Approved", value: 420 },
  { name: "Pending", value: 210 },
  { name: "Rejected", value: 115 },
  { name: "Under Review", value: 175 },
];

const PIE_COLORS = ["#10b981", "#f59e0b", "#ef4444", "#6366f1"];

// ── Stat Card ──────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, change, color }) => (
  <div className={`stat-card stat-card--${color}`}>
    <div className="stat-card__icon">
      <Icon />
    </div>
    <div className="stat-card__body">
      <p className="stat-card__label">{label}</p>
      <p className="stat-card__value">{value}</p>
      <p className={`stat-card__change ${change >= 0 ? "up" : "down"}`}>
        {change >= 0 ? "▲" : "▼"} {Math.abs(change)}% vs last month
      </p>
    </div>
  </div>
);

// ── Status Badge ───────────────────────────────────────────
const Badge = ({ status }) => {
  let displayStatus = "Pending";
  let statusClass = "pending";
  
  if (status === "experienced" || status === "Experienced") {
    displayStatus = "Experienced";
    statusClass = "approved";
  } else if (status === "fresher" || status === "Fresher") {
    displayStatus = "Fresher";
    statusClass = "under-review";
  }
  
  return (
    <span className={`badge badge--${statusClass}`}>{displayStatus}</span>
  );
};

// ── Custom Tooltip ─────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="chart-tooltip">
        <p className="chart-tooltip__label">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }}>
            {p.name}: <strong>{p.value.toLocaleString()}</strong>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// ── Applicant Details Modal ─────────────────────────────────
const ApplicantModal = ({ applicant, onClose }) => {
  if (!applicant) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Applicant Details</h3>
          <button className="modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>
        
        <div className="modal-content">
          <div className="modal-avatar">
            {applicant.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
          </div>
          
          <h4 className="modal-name">{applicant.name}</h4>
          
          <div className="modal-info-grid">
            <div className="modal-info-item">
              <FaRole className="modal-info-icon" />
              <div>
                <span className="modal-info-label">Role Applied</span>
                <p className="modal-info-value">{applicant.role}</p>
              </div>
            </div>
            
            <div className="modal-info-item">
              <FaCalendarAlt className="modal-info-icon" />
              <div>
                <span className="modal-info-label">Application Date</span>
                <p className="modal-info-value">{applicant.date}</p>
              </div>
            </div>
            
            <div className="modal-info-item">
              <FaEnvelope className="modal-info-icon" />
              <div>
                <span className="modal-info-label">Email</span>
                <p className="modal-info-value">{applicant.email || 'N/A'}</p>
              </div>
            </div>
            
            <div className="modal-info-item">
              <FaPhone className="modal-info-icon" />
              <div>
                <span className="modal-info-label">Mobile</span>
                <p className="modal-info-value">{applicant.mobile || 'N/A'}</p>
              </div>
            </div>
            
            <div className="modal-info-item">
              <FaMapMarkerAlt className="modal-info-icon" />
              <div>
                <span className="modal-info-label">City</span>
                <p className="modal-info-value">{applicant.city || 'N/A'}</p>
              </div>
            </div>
            
            <div className="modal-info-item">
              <FaUserGraduate className="modal-info-icon" />
              <div>
                <span className="modal-info-label">Experience</span>
                <p className="modal-info-value">
                  {applicant.experience ? `${applicant.experience} years` : 'Fresher'}
                </p>
              </div>
            </div>
            
            <div className="modal-info-item">
              <FaBuilding className="modal-info-icon" />
              <div>
                <span className="modal-info-label">Current Company</span>
                <p className="modal-info-value">{applicant.company || 'N/A'}</p>
              </div>
            </div>
            
            <div className="modal-info-item">
              <FaBriefcase className="modal-info-icon" />
              <div>
                <span className="modal-info-label">Current Designation</span>
                <p className="modal-info-value">{applicant.designation || 'N/A'}</p>
              </div>
            </div>
          </div>
          
          <div className="modal-status">
            <Badge status={applicant.status} />
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Main Dashboard ─────────────────────────────────────────
const Dashboard = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [isLoading, setIsLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(true);
  const [showCharts, setShowCharts] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  
  // State for the fetched dashboard data
  const [dashboardCounts, setDashboardCounts] = useState({
    applicants: 0,
    experienced: 0,
    freshers: 0,
    roles: 0,
    blogs: 0,
  });

  // State for dynamic applicants table
  const [dynamicApplicants, setDynamicApplicants] = useState([]);
  const [jobMapping, setJobMapping] = useState({});

  // State for the animated values
  const [animatedStats, setAnimatedStats] = useState({
    applicants: 0,
    experienced: 0,
    freshers: 0,
    roles: 0,
    blogs: 0,
  });

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // ── CSV Export Function with S.No ───────────────────────────
  const exportToCSV = () => {
    // Prepare data for CSV with S.No
    const csvData = dynamicApplicants.map((applicant, index) => ({
      'S.No': index + 1,
      'Name': applicant.name,
      'Role Applied': applicant.role,
      'Application Date': applicant.date,
      'Status': applicant.status,
      'Email': applicant.email || 'N/A',
      'Mobile': applicant.mobile || 'N/A',
      'City': applicant.city || 'N/A',
      'Experience': applicant.experience ? `${applicant.experience} years` : 'Fresher',
      'Current Company': applicant.company || 'N/A',
      'Current Designation': applicant.designation || 'N/A'
    }));

    // Define CSV headers
    const headers = [
      'S.No',
      'Name', 
      'Role Applied', 
      'Application Date', 
      'Status', 
      'Email', 
      'Mobile', 
      'City', 
      'Experience', 
      'Current Company', 
      'Current Designation'
    ];

    // Convert to CSV format
    const csvRows = [];
    csvRows.push(headers.join(','));
    
    for (const row of csvData) {
      const values = headers.map(header => {
        const value = row[header] || '';
        // Wrap in quotes if contains comma
        return `"${String(value).replace(/"/g, '""')}"`;
      });
      csvRows.push(values.join(','));
    }

    // Download CSV file
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `applicants_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ── API Call Functions ─────────────────────────────────────
  const fetchDashboardCounts = async () => {
    try {
      console.log("Fetching from:", SummaryApi.dashboardcounts.url);
      
      const response = await fetch(SummaryApi.dashboardcounts.url, {
        method: SummaryApi.dashboardcounts.method,
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("API Response:", result);

      if (result.status) {
        setDashboardCounts({
          applicants: Number(result.data.total_job_applicants_count || 0),
          experienced: Number(result.data.experienced_counts || 0),
          freshers: Number(result.data.fresher_counts || 0),
          roles: Number(result.data.total_active_roles || 0),
          blogs: Number(result.data.total_blogs_count || 0),
        });
      } else {
        console.error("API returned status false:", result);
      }
    } catch (error) {
      console.error("Dashboard Count Error:", error);
      setDashboardCounts({
        applicants: 0,
        experienced: 0,
        freshers: 0,
        roles: 0,
        blogs: 0,
      });
    }
  };

  // Fetch jobs and applicants for dynamic table
  const fetchApplicantsTable = async () => {
    try {
      setTableLoading(true);
      
      // 1. Fetch Jobs for role mapping
      const jobResponse = await fetch(SummaryApi.getCareers.url);
      const jobResult = await jobResponse.json();
      const jobData = Array.isArray(jobResult) ? jobResult : jobResult.data || [];
      
      const mapping = {};
      jobData.forEach(job => {
        const id = job.id || job.job_id || job.career_id;
        mapping[String(id)] = job.job_title || "Unknown Role";
      });
      setJobMapping(mapping);

      // 2. Fetch Applicants
      const response = await fetch(SummaryApi.getApplicants.url);
      const result = await response.json();
      const rawApplicants = Array.isArray(result) ? result : result.data || [];
      
      // 3. Enrich with role titles and format for display
      const enrichedData = rawApplicants.map(app => ({
        id: app.job_applicant_id || app.id,
        name: app.full_name || "Unknown",
        role: mapping[String(app.job_id)] || "General Application",
        date: formatDate(app.created_date),
        rawDate: app.created_date,
        status: (app.experience && parseFloat(app.experience) > 0) ? "Experienced" : "Fresher",
        email: app.email,
        mobile: app.mobile_number,
        company: app.current_working_company,
        designation: app.current_designation,
        experience: app.experience,
        resume: app.resume,
        city: app.city
      }));
      
      // Sort by date (newest first)
      enrichedData.sort((a, b) => new Date(b.rawDate) - new Date(a.rawDate));
      
      setDynamicApplicants(enrichedData);
    } catch (error) {
      console.error("Error fetching applicants:", error);
      setDynamicApplicants([]);
    } finally {
      setTableLoading(false);
      setIsLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchDashboardCounts();
    fetchApplicantsTable();
  }, []);

  // Animate the stats when dashboardCounts updates
  useEffect(() => {
    const duration = 1200;
    const steps = 60;
    const interval = duration / steps;
    let step = 0;

    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const ease = 1 - Math.pow(1 - progress, 3);

      setAnimatedStats({
        applicants: Math.round(dashboardCounts.applicants * ease),
        experienced: Math.round(dashboardCounts.experienced * ease),
        freshers: Math.round(dashboardCounts.freshers * ease),
        roles: Math.round(dashboardCounts.roles * ease),
        blogs: Math.round(dashboardCounts.blogs * ease),
      });

      if (step >= steps) {
        clearInterval(timer);
      }
    }, interval);

    return () => clearInterval(timer);
  }, [dashboardCounts]);

  if (isLoading && tableLoading) {
    return (
      <div className="dashboard">
        <div className="dashboard__header">
          <div>
            <h1 className="dashboard__title">Admin Dashboard</h1>
            <p className="dashboard__subtitle">Loading dashboard data...</p>
          </div>
        </div>
        <div className="stats-grid">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="stat-card stat-card--loading">
              <div className="skeleton">Loading...</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Get recent applicants (last 7 entries for the table)
  const recentApplicants = dynamicApplicants.slice(0, 7);

  return (
    <div className="dashboard">
      {/* Header */}
      <div className="dashboard__header">
        <div>
          <h1 className="dashboard__title">Admin Dashboard</h1>
          <p className="dashboard__subtitle">Welcome back! Here's what's happening today.</p>
        </div>
        <div className="dashboard__header-actions">
          <span className="dashboard__date">
            <FaCalendarAlt /> {new Date().toLocaleDateString("en-IN", { dateStyle: "long" })}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="dashboard__tabs">
        {["overview", "applicants"].map((tab) => (
          <button
            key={tab}
            className={`tab-btn ${activeTab === tab ? "tab-btn--active" : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* ── STATS GRID (Updated with API data) ─────────────── */}
      <div className="stats-grid">
        <StatCard
          icon={FaUserTie}
          label="Total Applicants"
          value={animatedStats.applicants.toLocaleString()}
          change={12}
          color="blue"
        />
        <StatCard
          icon={FaTrophy}
          label="Experienced"
          value={animatedStats.experienced.toLocaleString()}
          change={8}
          color="purple"
        />
        <StatCard
          icon={FaGraduationCap}
          label="Freshers"
          value={animatedStats.freshers.toLocaleString()}
          change={5}
          color="green"
        />
        <StatCard
          icon={FaBriefcase}
          label="Active Roles"
          value={animatedStats.roles.toLocaleString()}
          change={3}
          color="amber"
        />
        <StatCard
          icon={FaNewspaper}
          label="Total Blogs"
          value={animatedStats.blogs.toLocaleString()}
          change={-2}
          color="blue"
        />
      </div>

      {/* Charts Section - Hidden by default */}
      {showCharts && (
        <>
          <div className="charts-row">
            <div className="chart-card chart-card--wide">
              <div className="chart-card__header">
                <h3 className="chart-card__title">Monthly Visitors & Applications</h3>
                <span className="chart-card__badge">2026</span>
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={monthlyData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#6b7280" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: "#6b7280" }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                  <Bar dataKey="visitors" name="Visitors" fill="#6366f1" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="applications" name="Applications" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-card">
              <div className="chart-card__header">
                <h3 className="chart-card__title">Application Status</h3>
                <span className="chart-card__badge trend-up">↑ 12%</span>
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      {/* ── DYNAMIC TABLE with S.No ─────────────────────────────── */}
      <div className="table-card">
        <div className="table-card__header">
          <h3 className="table-card__title">
            {activeTab === "applicants" ? "All Applicants" : "Recent Applicants"}
          </h3>
          <div style={{ display: "flex", gap: "10px" }}>
            <button 
              className="btn btn--export" 
              onClick={exportToCSV}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 16px",
                background: "#10b981",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "14px"
              }}
            >
              <FaFileExcel /> Export CSV
            </button>
            {activeTab !== "applicants" && (
              <button 
                className="btn btn--ghost" 
                onClick={() => setActiveTab("applicants")}
              >
                View All →
              </button>
            )}
          </div>
        </div>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Name</th>
                <th>Role Applied</th>
                <th>Application Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {tableLoading ? (
                <tr>
                  <td colSpan="6" className="table-loading">
                    Loading applicants...
                   </td>
                 </tr>
              ) : (activeTab === "applicants" ? dynamicApplicants : recentApplicants).length > 0 ? (
                (activeTab === "applicants" ? dynamicApplicants : recentApplicants).map((applicant, index) => (
                  <tr key={applicant.id || index}>
                    <td className="data-table__sno">{index + 1}</td>
                    <td>
                      <div className="data-table__name">
                        <div className="avatar">
                          {applicant.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                        </div>
                        {applicant.name}
                      </div>
                     </td>
                    <td className="data-table__role">{applicant.role}</td>
                    <td className="data-table__date">{applicant.date}</td>
                    <td>
                      <Badge status={applicant.status} />
                     </td>
                    <td>
                      <button 
                        className="btn-action"
                        onClick={() => setSelectedApplicant(applicant)}
                      >
                        <FaEye style={{ marginRight: '4px' }} /> View
                      </button>
                     </td>
                   </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="table-empty">
                    No applicants found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Applicant Details Modal */}
      {selectedApplicant && (
        <ApplicantModal 
          applicant={selectedApplicant} 
          onClose={() => setSelectedApplicant(null)} 
        />
      )}
    </div>
  );
};

export default Dashboard;